import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { noteService } from '@/services/note.service';
import { leadKeys, contactKeys, dealKeys, companyKeys, noteKeys } from '@/lib/query-keys';

export function useAllNotes() {
  const notesQuery = useQuery({
    queryKey: noteKeys.all,
    queryFn: () => noteService.getAll(),
  });
  return { notes: notesQuery.data?.data ?? [], isLoading: notesQuery.isLoading };
}

export function useNotes() {
  const queryClient = useQueryClient();

  const invalidateRelated = (entityType?: string, entityId?: string) => {
    queryClient.invalidateQueries({ queryKey: noteKeys.all });
    if (entityType && entityId) {
      const keyMap: Record<string, (id: string) => readonly unknown[]> = {
        lead: leadKeys.notes,
        contact: contactKeys.notes,
        deal: dealKeys.notes,
        company: companyKeys.notes,
      };
      const keyFn = keyMap[entityType];
      if (keyFn) {
        queryClient.invalidateQueries({ queryKey: keyFn(entityId) });
      }
    }
  };

  const createNote = useMutation({
    mutationFn: (data: Parameters<typeof noteService.create>[0]) => noteService.create(data),
    onSuccess: (_, data) => {
      const entityType = data.leadId ? 'lead' : data.contactId ? 'contact' : data.dealId ? 'deal' : data.companyId ? 'company' : undefined;
      const entityId = data.leadId || data.contactId || data.dealId || data.companyId;
      invalidateRelated(entityType, entityId);
      toast.success('Note created');
    },
    onError: () => toast.error('Failed to create note'),
  });

  const updateNote = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof noteService.update>[1] }) => noteService.update(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: noteKeys.all }); toast.success('Note updated'); },
    onError: () => toast.error('Failed to update note'),
  });

  const deleteNote = useMutation({
    mutationFn: ({ id }: { id: string; entityType?: string; entityId?: string }) => noteService.delete(id),
    onSuccess: (_, { entityType, entityId }) => { invalidateRelated(entityType, entityId); toast.success('Note deleted'); },
    onError: () => toast.error('Failed to delete note'),
  });

  const togglePin = useMutation({
    mutationFn: ({ id }: { id: string; entityType?: string; entityId?: string }) => noteService.togglePin(id),
    onSuccess: (_, { entityType, entityId }) => { invalidateRelated(entityType, entityId); toast.success('Note pin toggled'); },
    onError: () => toast.error('Failed to toggle pin'),
  });

  return { createNote, updateNote, deleteNote, togglePin };
}
